import axios from 'axios'

const API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

/**
 * Process a CAD command to create or modify 3D models.
 * @param {string} token - The authentication token.
 * @param {string} command - The natural language CAD command.
 * @param {string|null} sessionId - Optional session ID for continuing an existing session.
 * @returns {Promise<object>} - A promise that resolves to the CAD response data.
 */
export const processCADCommand = async (token, command, sessionId = null) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/cad/command`,
			{
				command,
				session_id: sessionId,
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			}
		)
		return response.data
	} catch (error) {
		console.error('Error processing CAD command:', error)
		throw error
	}
}

/**
 * Undo the last CAD operation in a session.
 * @param {string} token - The authentication token.
 * @param {string} sessionId - The session ID.
 * @returns {Promise<object>} - A promise that resolves to the CAD response data.
 */
export const undoCAD = async (token, sessionId) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/cad/undo`,
			{
				session_id: sessionId,
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			}
		)
		return response.data
	} catch (error) {
		console.error('Error undoing CAD operation:', error)
		throw error
	}
}

/**
 * Redo a previously undone CAD operation in a session.
 * @param {string} token - The authentication token.
 * @param {string} sessionId - The session ID.
 * @returns {Promise<object>} - A promise that resolves to the CAD response data.
 */
export const redoCAD = async (token, sessionId) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/cad/redo`,
			{
				session_id: sessionId,
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			}
		)
		return response.data
	} catch (error) {
		console.error('Error redoing CAD operation:', error)
		throw error
	}
}

/**
 * Get the status of a CAD session.
 * @param {string} token - The authentication token.
 * @param {string} sessionId - The session ID.
 * @returns {Promise<object>} - A promise that resolves to the session data.
 */
export const getCADSession = async (token, sessionId) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/cad/session/${sessionId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		)
		return response.data
	} catch (error) {
		console.error('Error fetching CAD session:', error)
		throw error
	}
}

/**
 * Delete a CAD session.
 * @param {string} token - The authentication token.
 * @param {string} sessionId - The session ID.
 * @returns {Promise<object>} - A promise that resolves to the deletion confirmation.
 */
export const deleteCADSession = async (token, sessionId) => {
	try {
		const response = await axios.delete(
			`${API_BASE_URL}/cad/session/${sessionId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		)
		return response.data
	} catch (error) {
		console.error('Error deleting CAD session:', error)
		throw error
	}
}

/**
 * Get the download URL for a CAD file in a specific format.
 * @param {string} sessionId - The session ID.
 * @param {string} format - The file format (glb, stl, step, svg, dxf, json).
 * @returns {string} - The download URL.
 */
export const getCADFileURL = (sessionId, format) => {
	return `${API_BASE_URL}/cad/download/${sessionId}/${format}`
}

